import {Type} from '@angular/core';

import {UrlStore} from '../../url-store';
import {ApiDocsHelpComponent} from './content/api-docs-help.component';
import {BusinessRulesHelpComponent} from './content/business-rules-help.component';
import {ConfigurationHelpComponent} from './content/configuration-help.component';
import {DashboardHelpComponent} from './content/dashboard-help.component';
import {ForecastHelpComponent} from './content/forecast-help.component';
import {GroupSalesHelpComponent} from './content/group-sales-help.component';
import {InventoryHelpComponent} from './content/inventory-help.component';
import {PortfolioHelpComponent} from './content/portfolio-help.component';
import {RulePrioritiesHelpComponent} from './content/rule-priorities-help.component';
import {StatusHelpComponent} from './content/status-help.component';

/**
 * Maps a route to the component that documents it.
 *
 * Matching is by longest whole-segment prefix, so a child route inherits its
 * parent's help unless it registers its own -- the same rule the top bar uses to
 * pick the page title.
 *
 * Add a page by writing a content component (body markup only -- the dialog
 * chrome and the Okay button come from HelpDialogComponent), declaring it in
 * HelpDialogModule, and adding a line here.
 *
 * Business Rules v2 is deliberately absent: it is superseded by the legacy
 * Business Rules screen and is not documented.
 */
export interface HelpTopic {
    route: string;
    content: Type<any>;
}

const ui = UrlStore.ui;

export const HELP_TOPICS: HelpTopic[] = [
    {route: `${ui.landing}${ui.dashboard}`, content: DashboardHelpComponent},
    {route: `${ui.landing}${ui.forecast}`, content: ForecastHelpComponent},
    {route: `${ui.landing}${ui.inventory}`, content: InventoryHelpComponent},
    {route: `${ui.landing}${ui.groupSales}`, content: GroupSalesHelpComponent},
    {route: `${ui.landing}${ui.businessRules}`, content: BusinessRulesHelpComponent},
    {
        route: `${ui.landing}${ui.businessRules}/business-rules-priorities`,
        content: RulePrioritiesHelpComponent
    },
    {route: `${ui.landing}${ui.status}`, content: StatusHelpComponent},
    {route: `${ui.landing}${ui.configuration}`, content: ConfigurationHelpComponent},
    {route: `${ui.landing}${ui.apiDocs}`, content: ApiDocsHelpComponent},
    /* Documents the cross-buyer prototype, which lands with the portfolio screen
       itself. Registered ahead of it: an entry for a route nobody can reach is
       inert, whereas a screen shipping without help is what this registry exists
       to prevent. */
    {route: `${ui.landing}${ui.portfolio}`, content: PortfolioHelpComponent}
];

export function resolveHelpContent(url: string): Type<any> | null {
    if (!url) {
        return null;
    }
    /* Match whole path segments, not raw string prefixes: /business-rules-v2
       starts with /business-rules but is a different screen entirely. Query
       strings and fragments are trimmed first. */
    const path = url.split(/[?#]/)[0];
    const match = HELP_TOPICS
        .filter(topic => path === topic.route || path.startsWith(`${topic.route}/`))
        .sort((a, b) => b.route.length - a.route.length)[0];
    return match ? match.content : null;
}
